import React from "react";
import { useParams } from "react-router-dom";
import { Card, Col, Row, Table, Typography } from "antd";
import { ColumnsType } from 'antd/es/table';
import { useGetApplicationsQuery, useGetUsersByLicenseQuery } from "../api/api";
import Spinner from "../components/Spinner";
import { Error } from "../components/Error.component";

const { Title } = Typography;

export const AppDetails = () => {
  const { AppId } = useParams(); // Get the AppId from the route parameters
  const { data, error, isLoading } = useGetApplicationsQuery(AppId); // Fetch application data
  const { data: usersData, error: usersError, isLoading: usersLoading } = useGetUsersByLicenseQuery(AppId); // Fetch users data

  const app = data?.data?.find(item => item.id === AppId) || {}; // Find the application matching the AppId

  // Define columns for the licenses table
  const columns: ColumnsType<any> = [
    { title: "License Name", dataIndex: "licensename_0", key: "licensename_0", className: "table-cell-padding" },
    { title: "Description", dataIndex: "description", key: "description", className: "table-cell-padding" },
    { title: "Unit license Cost (Year)", dataIndex: "costperlicenseperannum_0", key: "costperlicenseperannum_0", render: (value) => (
      <div style={{ textAlign: 'right' }}>
        {`$${Math.round(parseFloat(value)).toLocaleString()}`}
      </div>
    ), className: "table-cell-padding" },
    {
      title: "Usage Count",
      dataIndex: "usageCount",
      key: "usageCount",
      className: "table-cell-padding",
      render: (value) => (
        <div style={{ textAlign: 'right' }}>
          {value.toLocaleString()}
        </div>
      )
    },
    { title: "Utilization (%)", key: "utilizationPercentage", sorter: (a, b) => {
      const aUtilization = parseFloat(a.usageCount);
      const aPurchased = parseFloat(a.quantitypurchasedlicenses_0);
      const aPercentage = aPurchased ? Math.round((aUtilization / aPurchased) * 100) : 0;
      const bUtilization = parseFloat(b.usageCount);
      const bPurchased = parseFloat(b.quantitypurchasedlicenses_0);
      const bPercentage = bPurchased ? Math.round((bUtilization / bPurchased) * 100) : 0;
      return aPercentage - bPercentage;
    }, render: (text, record) => {
      const utilization = parseFloat(record.usageCount);
      const purchased = parseFloat(record.quantitypurchasedlicenses_0);
      const percentage = purchased ? Math.round((utilization / purchased) * 100) : 0;
      let color = "green";
      let fontWeight = "normal";
      if (percentage >= 90) {
        color = "red";
        fontWeight = "bold";
      } else if (percentage >= 80) {
        color = "orange";
      }
      return <span style={{ color, fontWeight }}>{`${percentage}%`}</span>;
    }, className: "table-cell-padding" },
  ];

  // Define columns for the users table
  const userColumns = [
    { title: "User Name", dataIndex: "userName", key: "userName", className: "table-cell-padding" },
    { title: "Email", dataIndex: "email", key: "email", className: "table-cell-padding" },
    { title: "Department", dataIndex: "department", key: "department", className: "table-cell-padding" },
    { title: "Role", dataIndex: "role", key: "role", className: "table-cell-padding" },
  ];

  // Format start and end dates
  const startDate = new Date(app.startdateinvoice_0).toLocaleDateString();
  const endDate = new Date(app.enddateinvoice_0).toLocaleDateString();

  return (
    <div className="montserrat-font">
      {isLoading && <Spinner />} {/* Show spinner while loading */}
      {error && <Error error={"Oops"} />} {/* Show error message if there's an error */}
      {data && !error && (
        <div>
          <Row gutter={32}>
            <Col xs={24} sm={12}>
              <Card title="Application Details" className="muse-card" style={{ width: "100%" }}>
                <h3>Publisher Name: <strong>{app.publishername_0}</strong></h3>
                <h3>Vendor Name: <strong>{app.vendorname_0}</strong></h3>
                <h3>Contract ID: <strong>{app.contractnumberoracle_0}</strong></h3>
                <h3>Contract Start/end date: <strong>{startDate} - {endDate}</strong></h3>
                <h3>Notice Period: <strong>{app.noticeperiodcontract_0} Days</strong></h3>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card title="License overview" className="muse-card" style={{ width: "100%" }}>
                <h3>License Name: <strong>{app.licensename_0}</strong></h3>
                <h3>Number of licenses: <strong>{app.quantitypurchasedlicenses_0}</strong></h3>
                <h3>Total license cost: <strong>${app.costperlicenseperannum_0 && app.quantitypurchasedlicenses_0 ? Math.round(app.costperlicenseperannum_0 * app.quantitypurchasedlicenses_0).toLocaleString() : 'N/A'} / Year</strong></h3>
                <h3>Cost Department: <strong>{app.bu}</strong></h3>
                <h3>Type of License: <strong>{app.typeoflicense_0}</strong></h3>
              </Card>
            </Col>
          </Row>
          <Row>
            <Card className="muse-card" style={{ width: "100%" }}>
              <Table
                columns={columns}
                dataSource={[{ ...app, key: AppId }]} // Add a unique key to each item
                className="montserrat-font"
                rowClassName={() => "table-row-spacing"}
              />
            </Card>
          </Row>
          {/* Commented out section for users using this license */}
          {/* 
          <Row>
            <Card title="Users Using This License" className="muse-card" style={{ width: "100%" }}>
              {usersLoading && <Spinner />}
              {usersError && <Error error={"Oops"} />}
              {usersData && !usersError && (
                <Table
                  columns={userColumns}
                  dataSource={usersData.data}
                  className="montserrat-font"
                  rowClassName={() => "table-row-spacing"}
                />
              )}
            </Card>
          </Row>
          */}
        </div>
      )}
    </div>
  );
};
